import { FC, useState } from 'react';
import { SpaceBetween, Textarea, Button, Box, ProgressBar } from '@amzn/awsui-components-react';

interface BatchQueryInputProps {
  onSubmit: (queries: string[]) => Promise<void>;
  disabled?: boolean;
  progress: { sent: number; total: number } | null;
}

const BatchQueryInput: FC<BatchQueryInputProps> = ({ onSubmit, disabled, progress }) => {
  const [queries, setQueries] = useState<string[]>(['']);

  const nonEmptyCount = queries.filter((q) => q.trim()).length;
  const isSubmitting = !!progress;
  const inputDisabled = disabled || isSubmitting;

  const handleChange = (index: number, value: string) => {
    if (value.includes('\n')) {
      const lines = value.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
      setQueries((prev) => [...prev.slice(0, index), ...lines, ...prev.slice(index + 1)]);
    } else {
      setQueries((prev) => prev.map((q, i) => (i === index ? value : q)));
    }
  };

  const addRow = () => setQueries((prev) => [...prev, '']);

  const removeRow = (index: number) => setQueries((prev) => prev.filter((_, i) => i !== index));

  const handleSubmit = async () => {
    const nonEmpty = queries.map((q) => q.trim()).filter(Boolean);
    if (!nonEmpty.length) return;
    await onSubmit(nonEmpty);
    setQueries(['']);
  };

  return (
    <SpaceBetween size="s">
      {queries.map((q, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
          <Box padding={{ top: 'xs' }}>{i + 1}.</Box>
          <div style={{ flex: 1 }}>
            <Textarea
              value={q}
              onChange={({ detail }) => handleChange(i, detail.value)}
              placeholder="Enter a query (paste multiple lines to auto-split)..."
              disabled={inputDisabled}
              rows={1}
            />
          </div>
          {queries.length > 1 && (
            <Box padding={{ top: 'xxs' }}>
              <Button iconName="remove" variant="icon" onClick={() => removeRow(i)} disabled={inputDisabled} />
            </Box>
          )}
        </div>
      ))}

      {progress && (
        <ProgressBar
          value={progress.total ? (progress.sent / progress.total) * 100 : 0}
          additionalInfo={`Submitting: ${progress.sent} of ${progress.total}`}
        />
      )}

      <Box float="right">
        <SpaceBetween direction="horizontal" size="xs">
          <Button iconName="add-plus" onClick={addRow} disabled={inputDisabled}>
            Add query
          </Button>
          <Button onClick={() => setQueries([''])} disabled={inputDisabled || queries.length === 1 && !queries[0]}>
            Clear
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={!nonEmptyCount || inputDisabled}
            loading={isSubmitting}
          >
            {`Send ${nonEmptyCount || ''}`}
          </Button>
        </SpaceBetween>
      </Box>
    </SpaceBetween>
  );
};

export default BatchQueryInput;
